<script setup lang="ts">
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { computed, useTemplateRef, watch } from "vue";
import { usePointerRowDrag } from "../composables/usePointerRowDrag";
import { assertNever } from "../lib/assertNever";
import type { EntityArcMoment } from "../lib/entityFrontmatter";
import {
  newArcMomentId,
  nextArcOrder,
  reorderArcMoment,
  sortArcMoments,
} from "../lib/entityArc";
import {
  MANUSCRIPT_SCENE_NONE,
  manuscriptSceneLabel,
  manuscriptSceneSelectOptions,
} from "../lib/manuscriptScenes";
import type { SceneOpening } from "../lib/sceneLabel";
import { openContextMenu } from "../lib/platform/contextMenu";
import { useTea } from "../lib/tea";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { useSceneOpeningsStore } from "../stores/useSceneOpeningsStore";
import {
  initialEntityArcDialogModel,
  updateEntityArcDialogFn,
  type EntityArcDialogCmd,
} from "./EntityArcPanel.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwSelect from "./ui/ZwSelect.vue";
import ZwTextarea from "./ui/ZwTextarea.vue";

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const entityFields = useActiveEntityFieldsStore();
const sceneOpenings = useSceneOpeningsStore();

const chapters = computed(() => session.chapters);

function openingFor(path: string): SceneOpening {
  return sceneOpenings.openingFor(path);
}

const arcMoments = computed(() => sortArcMoments(entityFields.fields.arc));

const listRoot = useTemplateRef<HTMLElement>("listRoot");

const drag = usePointerRowDrag({
  rowAtPoint(clientX, clientY) {
    const root = listRoot.value;
    if (root === null) return nothing;
    for (const el of root.querySelectorAll<HTMLElement>("[data-row-key]")) {
      const rect = el.getBoundingClientRect();
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        continue;
      }
      return toMaybe(el.dataset.rowKey);
    }
    return nothing;
  },
  onDrop(from, toKey) {
    entityFields.setField(
      "arc",
      reorderArcMoment(entityFields.fields.arc, from.key, toKey),
      { immediate: true },
    );
  },
});

function runArcDialogCmd(cmd: EntityArcDialogCmd): void {
  switch (cmd.kind) {
    case "persistMoment": {
      const existing = matchMaybe(cmd.editingMomentId, {
        nothing: (): Maybe<EntityArcMoment> => nothing,
        just: (id): Maybe<EntityArcMoment> =>
          toMaybe(entityFields.fields.arc.find((entry) => entry.id === id)),
      });
      const nextMoment: EntityArcMoment = {
        id: matchMaybe(cmd.editingMomentId, {
          nothing: () => newArcMomentId(),
          just: (id) => id,
        }),
        order: matchMaybe(existing, {
          just: (entry) => entry.order,
          nothing: () => nextArcOrder(entityFields.fields.arc),
        }),
        at: cmd.at.length > 0 ? just(cmd.at) : nothing,
        moment: cmd.text,
        link:
          cmd.sceneLink.length > 0 ? just({ file: cmd.sceneLink }) : nothing,
      };
      const editingId = cmd.editingMomentId;
      const nextArc = isJust(editingId)
        ? entityFields.fields.arc.map((entry) =>
            entry.id === editingId.value ? nextMoment : entry,
          )
        : [...entityFields.fields.arc, nextMoment];
      entityFields.setField("arc", nextArc, { immediate: true });
      return;
    }
    case "removeMoment":
      entityFields.setField(
        "arc",
        entityFields.fields.arc.filter((entry) => entry.id !== cmd.momentId),
        { immediate: true },
      );
      void dispatchArc({ type: "MomentRemoved", momentId: cmd.momentId });
      return;
    default:
      assertNever(cmd);
  }
}

const { model: arcModel, dispatch: dispatchArc } = useTea(
  initialEntityArcDialogModel,
  updateEntityArcDialogFn,
  { runCmd: just(runArcDialogCmd) },
);

const momentDialogOpen = computed(() => arcModel.value.open);
const editingMomentId = computed(() => arcModel.value.editingMomentId);
const momentAt = computed({
  get: (): string => arcModel.value.at,
  set: (at: string): void => {
    void dispatchArc({ type: "AtChanged", at });
  },
});
const momentText = computed({
  get: (): string => arcModel.value.text,
  set: (text: string): void => {
    void dispatchArc({ type: "TextChanged", text });
  },
});
const momentSceneLink = computed({
  get: (): string => arcModel.value.sceneLink,
  set: (sceneLink: string): void => {
    void dispatchArc({ type: "SceneLinkChanged", sceneLink });
  },
});

watch(
  () => session.manuscript,
  () => {
    sceneOpenings.ensureLoaded();
  },
  { immediate: true },
);

const sceneLinkOptions = computed(() => {
  const openings = sceneOpenings.openingsByPath;
  const options = manuscriptSceneSelectOptions(chapters.value, (path) => {
    void openings[path];
    return openingFor(path);
  });
  const current = momentSceneLink.value.trim();
  if (
    current.length > 0 &&
    options.some((option) => option.value === current) === false
  ) {
    options.push({
      value: current,
      label: `${manuscriptSceneLabel(current, chapters.value, openingFor)} (not in project)`,
    });
  }
  return options;
});

function sceneLabel(path: string): string {
  return manuscriptSceneLabel(path, chapters.value, openingFor);
}

function openCreateMoment(): void {
  void dispatchArc({ type: "OpenCreate" });
}

function openEditMoment(arcMoment: EntityArcMoment): void {
  void dispatchArc({
    type: "OpenEdit",
    momentId: arcMoment.id,
    at: matchMaybe(arcMoment.at, {
      just: (value) => value,
      nothing: () => "",
    }),
    text: arcMoment.moment,
    sceneLink: matchMaybe(arcMoment.link, {
      just: (link) => link.file,
      nothing: () => MANUSCRIPT_SCENE_NONE,
    }),
  });
}

function closeMomentDialog(): void {
  void dispatchArc({ type: "Close" });
}

function commitMoment(): void {
  void dispatchArc({ type: "Commit" });
}

function removeEditingMoment(): void {
  void dispatchArc({ type: "RemoveEditing" });
}

function removeMoment(momentId: string): void {
  entityFields.setField(
    "arc",
    entityFields.fields.arc.filter((entry) => entry.id !== momentId),
    { immediate: true },
  );
  void dispatchArc({ type: "MomentRemoved", momentId });
}

function openLinkedScene(path: string): void {
  void workspace.onSelectProjectFile(path);
}

function openLinkedSceneFromDialog(): void {
  const path = momentSceneLink.value.trim();
  if (path.length === 0) return;
  void workspace.onSelectProjectFile(path);
}

function isDraggingArcMoment(momentId: string): boolean {
  const from = drag.dragFrom.value;
  return isJust(from) && from.value.key === momentId;
}

function isDragOverArcMoment(momentId: string): boolean {
  const over = drag.dragOver.value;
  const from = drag.dragFrom.value;
  return (
    isJust(over) &&
    over.value === momentId &&
    (isNothing(from) || from.value.key !== momentId)
  );
}

async function onMomentContextMenu(
  event: MouseEvent,
  arcMoment: EntityArcMoment,
): Promise<void> {
  const items: Parameters<typeof openContextMenu>[1] = [];
  const linkedFile = matchMaybe(arcMoment.link, {
    just: (link) => link.file,
    nothing: () => "",
  });
  if (linkedFile.length > 0) {
    items.push({
      id: `open-scene-${arcMoment.id}`,
      label: "Open linked scene",
      action: () => openLinkedScene(linkedFile),
    });
  }
  items.push({
    id: `remove-moment-${arcMoment.id}`,
    label: "Remove event",
    action: () => removeMoment(arcMoment.id),
  });

  await openContextMenu(event, items);
}
</script>

<template>
  <section class="entity-arc-section">
    <div class="entity-arc-header">
      <h3>Arc</h3>
      <ZwButton size="sm" @click="openCreateMoment">+ Add event</ZwButton>
    </div>
    <p v-if="arcMoments.length === 0" class="entity-arc-empty">
      No events yet.
    </p>
    <ol v-else ref="listRoot" class="entity-arc-list">
      <li
        v-for="(arcMoment, index) in arcMoments"
        :key="arcMoment.id"
        class="entity-arc-item"
        :class="{
          'entity-arc-item-drag': isDraggingArcMoment(arcMoment.id),
          'entity-arc-item-over': isDragOverArcMoment(arcMoment.id),
        }"
        :data-row-key="arcMoment.id"
        @contextmenu="onMomentContextMenu($event, arcMoment)"
        @pointerdown="drag.onRowPointerDown({ key: arcMoment.id }, $event)"
      >
        <div class="entity-arc-row">
          <span class="entity-arc-order" aria-hidden="true">{{
            index + 1
          }}</span>
          <div class="entity-arc-content">
            <button
              type="button"
              class="entity-arc-main"
              @click="openEditMoment(arcMoment)"
            >
              <span class="entity-arc-moment">{{ arcMoment.moment }}</span>
              <span v-if="isJust(arcMoment.at)" class="entity-arc-at">{{
                arcMoment.at.value
              }}</span>
            </button>
            <button
              v-if="
                isJust(arcMoment.link) && arcMoment.link.value.file.length > 0
              "
              type="button"
              class="entity-arc-link"
              :title="sceneLabel(arcMoment.link.value.file)"
              @click.stop="openLinkedScene(arcMoment.link.value.file)"
            >
              {{ sceneLabel(arcMoment.link.value.file) }}
            </button>
          </div>
        </div>
      </li>
    </ol>
  </section>

  <ZwDialog
    :open="momentDialogOpen"
    :title="isJust(editingMomentId) ? 'Edit event' : 'Add event'"
    :description="nothing"
    @close="closeMomentDialog"
  >
    <ZwTextarea
      :id="nothing"
      v-model="momentText"
      :label="just('Event')"
      autosize
      :rows="2"
    />
    <ZwInput
      :id="nothing"
      v-model="momentAt"
      :label="just('When')"
      placeholder="Optional"
    />
    <ZwSelect
      :id="nothing"
      v-model="momentSceneLink"
      :label="just('Linked scene')"
      :options="just(sceneLinkOptions)"
    />
    <div
      v-if="momentSceneLink.trim().length > 0"
      class="entity-arc-dialog-scene-actions"
    >
      <ZwButton size="sm" @click="openLinkedSceneFromDialog">
        Open scene
      </ZwButton>
    </div>
    <template #footer>
      <ZwButton
        v-if="isJust(editingMomentId)"
        class="entity-arc-dialog-remove"
        @click="removeEditingMoment"
      >
        Remove
      </ZwButton>
      <ZwButton @click="closeMomentDialog">Cancel</ZwButton>
      <ZwButton
        variant="primary"
        :disabled="momentText.trim().length === 0"
        @click="commitMoment"
      >
        {{ isJust(editingMomentId) ? "Save event" : "Add event" }}
      </ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.entity-arc-section {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: 1px solid var(--line);
}

.entity-arc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: 0.35rem;
}

.entity-arc-header h3 {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.entity-arc-empty {
  margin: 0 0.35rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.entity-arc-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.entity-arc-item {
  margin: 0.15rem 0;
  border-radius: 0.3rem;
}

.entity-arc-item-over {
  outline: 1px dashed
    color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
}

.entity-arc-item-drag {
  opacity: 0.55;
}

.entity-arc-row {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
  cursor: grab;
}

.entity-arc-item-drag .entity-arc-row,
.entity-arc-row:active {
  cursor: grabbing;
}

.entity-arc-order {
  flex-shrink: 0;
  width: 1.1rem;
  padding-top: 0.38rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  text-align: right;
}

.entity-arc-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.08rem;
}

.entity-arc-main {
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.08rem;
  padding: 0.35rem 0.1rem 0 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.entity-arc-moment {
  font-size: 0.88rem;
  color: var(--text);
  line-height: 1.35;
}

.entity-arc-at {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.entity-arc-link {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0 0.1rem 0.35rem;
  font: inherit;
  font-size: 0.75rem;
  color: color-mix(in oklch, var(--color-zw-amber) 75%, var(--text-muted));
  cursor: pointer;
  text-align: left;
}

.entity-arc-link:hover {
  color: var(--color-zw-amber);
  text-decoration: underline;
}

.entity-arc-dialog-scene-actions {
  margin-top: 0.35rem;
}

.entity-arc-dialog-remove {
  margin-right: auto;
  color: var(--danger);
}
</style>
